import { createBrowserRouter, RouterProvider, Navigate, Outlet, useParams } from 'react-router-dom';
import { Suspense, lazy, useEffect, useState } from 'react';
import { InterfaceProvider } from '@/providers/InterfaceProvider';
import { AuthProvider } from '@/hooks/useAuth';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { apiUrl } from '@/lib/api';
import { isIosDevice, isInAppBrowser, safariUrl } from '@/lib/device';
import { formatInTimeZone } from 'date-fns-tz';
import { de } from 'date-fns/locale';
import { QRCodeSVG } from 'qrcode.react';

// Lazy load pages
const LandingPage = lazy(() => import('@/pages/LandingPage'));
const MicrositeDemoPage = lazy(() => import('@/pages/MicrositeDemoPage'));
const LegalDocPage = lazy(() => import('@/pages/LegalDocPage'));
const DashboardPage = lazy(() => import('@/pages/InterfaceDashboard'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const EventsPage = lazy(() => import('@/pages/EventsPage'));
const EventDetailPage = lazy(() => import('@/pages/EventDetailPage'));
const EventSettingsPage = lazy(() => import('@/pages/EventSettingsPage'));
const NewEventPage = lazy(() => import('@/pages/NewEventPage'));
const GuestsPage = lazy(() => import('@/pages/GuestsPage'));
const NewGuestPage = lazy(() => import('@/pages/NewGuestPage'));
const GuestDetailPage = lazy(() => import('@/pages/GuestDetailPage'));
const GuestImportPage = lazy(() => import('@/pages/GuestImportPage'));
const EventInvitationsPage = lazy(() => import('@/pages/EventInvitationsPage'));
const QuickEditPage = lazy(() => import('@/pages/QuickEditPage'));
const EventMessagesPage = lazy(() => import('@/pages/EventMessagesPage'));
const EventSeatingPage = lazy(() => import('@/pages/EventSeatingPage'));
const EventAccommodationPage = lazy(() => import('@/pages/EventAccommodationPage'));
const EventCheckInPage = lazy(() => import('@/pages/EventCheckInPage'));
const EventPhotosPage = lazy(() => import('@/pages/EventPhotosPage'));
const PhotoUploadPage = lazy(() => import('@/pages/PhotoUploadPage'));
const TemplatesPage = lazy(() => import('@/pages/TemplatesPage'));
const EventTemplatesPage = lazy(() => import('@/pages/EventTemplatesPage'));
const TemplateDetailPage = lazy(() => import('@/pages/TemplateDetailPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const InboxPage = lazy(() => import('@/pages/InboxPage'));
const RsvpPage = lazy(() => import('@/pages/RsvpPage'));
const TicketPage = lazy(() => import('@/pages/TicketPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const SupplierSummaryPage = lazy(() => import('@/pages/SupplierSummaryPage'));

// Layout
const AppLayout = lazy(() => import('@/components/layout/AppLayout'));

// Loading component
function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

// Auth wrapper component
function AuthWrapper() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

// Short-link redirects used in WhatsApp template buttons: the approved Meta
// templates point at /r/:token (RSVP) and /m/:token (media gallery), which map
// to the real SPA routes /rsvp/:token and /p/:token.
function TokenRedirect({ base }: { base: string }) {
  const { token } = useParams<{ token: string }>();
  // /r/<token> is only ever used by WhatsApp template buttons; email links go
  // straight to /rsvp/<token>. Remember it so the RSVP response can tell the
  // server which channel the guest actually came from.
  if (base === '/rsvp') {
    try {
      sessionStorage.setItem('rsvp-source', 'whatsapp');
    } catch {
      // storage blocked — source stays "web"
    }
  }
  return <Navigate to={`${base}/${token ?? ''}`} replace />;
}

// Wallet short-links used in WhatsApp/email ticket delivery: /wa/:token and
// /wg/:token. Google returns JSON { saveUrl } (a normal web page that works in
// in-app browsers) which we fetch and forward to. Apple serves a .pkpass: we
// auto-open it in real browsers, but inside an in-app browser we show a landing
// page that guides the user to Safari (where Apple Wallet actually opens).
function WalletRedirect({ kind }: { kind: 'apple' | 'google' }) {
  const { token } = useParams<{ token: string }>();
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  // Once the .pkpass has been handed to the browser the page stays open behind
  // Wallet's "Hinzufügen" sheet — say goodbye instead of "loading…" forever.
  const [launched, setLaunched] = useState(false);
  const [eventDate, setEventDate] = useState<string | null>(null);
  const passUrl = apiUrl(`/api/wallet/apple/${token}`);
  const appleInApp = kind === 'apple' && isInAppBrowser();
  // Real Mobile Safari auto-loads the pass (the familiar "Hinzufügen" sheet);
  // only embedded browsers get the landing page. Detection keys on WhatsApp's
  // "[WAiOS/…]" UA marker, learned from the ticket-page logs.
  const appleIos = kind === 'apple' && isIosDevice() && appleInApp;
  // Apple Wallet only exists on iPhone/iPad. A Mac/PC/Android browser either
  // downloads the .pkpass to nowhere useful or dumps it as garbled text, so
  // show a QR code the guest scans with their iPhone camera instead.
  const appleNotIos = kind === 'apple' && !isIosDevice();
  const thisPageUrl =
    typeof window !== 'undefined' ? `${window.location.origin}/wallet/apple/${token}` : '';

  useEffect(() => {
    if (!token) return;
    fetch(apiUrl(`/api/rsvp/${token}`))
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { event?: { startAt?: string; timezone?: string } } | null) => {
        const startAt = d?.event?.startAt;
        if (!startAt) return;
        setEventDate(
          formatInTimeZone(
            new Date(startAt),
            d?.event?.timezone || 'Europe/Berlin',
            'd. MMMM yyyy',
            {
              locale: de,
            }
          )
        );
      })
      .catch(() => undefined);
  }, [token]);

  useEffect(() => {
    if (kind === 'apple') {
      if (appleInApp || appleNotIos || appleIos) return; // landing page instead of auto-loading
      window.location.replace(passUrl);
      // The download doesn't navigate away; flip the copy once Wallet had a
      // moment to take over.
      const t = window.setTimeout(() => setLaunched(true), 1500);
      return () => window.clearTimeout(t);
    }
    let cancelled = false;
    fetch(apiUrl(`/api/wallet/google/${token}`))
      .then(async (r) => {
        const data = (await r.json().catch(() => ({}))) as { saveUrl?: string; error?: string };
        if (!r.ok || !data.saveUrl) throw new Error(data.error || `HTTP ${r.status}`);
        if (!cancelled) window.location.replace(data.saveUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token, kind, appleInApp, appleNotIos, appleIos, passUrl]);

  if (appleNotIos) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-xl font-semibold">Ticket zu Apple Wallet hinzufügen</h1>
        <p className="max-w-sm text-sm text-muted-foreground">
          Apple Wallet gibt es nur auf dem iPhone. Scanne diesen Code mit der Kamera deines iPhones
          – das Ticket landet dann direkt in deiner Wallet.
        </p>
        <div className="rounded-xl bg-white p-4 shadow-sm">
          <QRCodeSVG value={thisPageUrl} size={192} level="M" />
        </div>
        <p className="max-w-sm text-xs text-muted-foreground">
          Alternativ öffnest du den Link aus deiner WhatsApp oder E-Mail direkt auf dem iPhone. Für
          den Einlass reicht auch der QR-Code auf der Ticketseite.
        </p>
        <a href={`/wa/${token}`} className="text-sm underline">
          Zurück zum Ticket
        </a>
      </div>
    );
  }

  if (appleIos) {
    const openInSafari = (
      <a
        href={safariUrl(thisPageUrl)}
        className={
          appleInApp
            ? 'inline-flex items-center justify-center rounded-md bg-black px-5 py-2.5 text-sm font-medium text-white'
            : 'text-sm underline'
        }
      >
        In Safari öffnen
      </a>
    );
    const loadPass = (
      <a
        href={passUrl}
        className={
          appleInApp
            ? 'text-sm underline'
            : 'inline-flex items-center justify-center rounded-md bg-black px-5 py-2.5 text-sm font-medium text-white'
        }
      >
        Zu Apple Wallet hinzufügen
      </a>
    );
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-xl font-semibold">Ticket zu Apple Wallet hinzufügen</h1>
        {appleInApp ? (
          <div className="max-w-sm rounded-lg border border-amber-200 bg-amber-50 p-4 text-left text-sm text-amber-900">
            <p className="font-medium">
              Dieser Browser in WhatsApp kann keine Wallet-Pässe öffnen.
            </p>
            <p className="mt-1">
              Tippe auf „In Safari öffnen" – dort erscheint „Hinzufügen". Falls nichts passiert:
              oben rechts auf <strong>⋯</strong> und <strong>„Im Browser öffnen"</strong> wählen.
            </p>
          </div>
        ) : (
          <p className="max-w-sm text-sm text-muted-foreground">
            In Safari erscheint nach dem Tipp „Hinzufügen". Siehst du diese Seite in WhatsApp oder
            einer anderen App, nimm „In Safari öffnen" – nur Safari kann Wallet-Pässe laden.
          </p>
        )}
        {appleInApp ? openInSafari : loadPass}
        {appleInApp ? loadPass : openInSafari}
        <button
          type="button"
          onClick={() => {
            navigator.clipboard?.writeText(thisPageUrl).then(
              () => setCopied(true),
              () => setCopied(false)
            );
          }}
          className="text-sm underline"
        >
          {copied ? 'Link kopiert – in Safari einfügen' : 'Link kopieren'}
        </button>
        <a href={`/wa/${token}`} className="text-xs text-muted-foreground underline">
          Zurück zum Ticket (QR-Code reicht am Einlass)
        </a>
      </div>
    );
  }

  if (launched && !failed) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 px-6 text-center">
        <h1 className="text-xl font-semibold">
          {eventDate ? `Wir sehen uns am ${eventDate}!` : 'Wir sehen uns beim Event!'}
        </h1>
        <p className="max-w-sm text-sm text-muted-foreground">
          Dein Ticket liegt jetzt in Apple Wallet. Du kannst diese Seite schließen.
        </p>
        <p className="max-w-sm text-xs text-muted-foreground">
          Kein „Hinzufügen" gesehen?{' '}
          <a href={passUrl} className="underline">
            Pass erneut laden
          </a>
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center px-6 text-center text-sm text-muted-foreground">
      {failed
        ? 'Das Ticket konnte nicht geladen werden. Bitte öffne den Link erneut oder kontaktiere den Veranstalter.'
        : 'Dein Ticket wird geladen…'}
    </div>
  );
}

const router = createBrowserRouter([
  {
    element: <AuthWrapper />,
    children: [
      {
        path: '/login',
        element: (
          <Suspense fallback={<PageLoader />}>
            <LoginPage />
          </Suspense>
        ),
      },
      {
        path: '/register',
        element: (
          <Suspense fallback={<PageLoader />}>
            <RegisterPage />
          </Suspense>
        ),
      },
      {
        path: '/s/:token',
        element: (
          <Suspense fallback={<PageLoader />}>
            <SupplierSummaryPage />
          </Suspense>
        ),
      },
      {
        // Standalone guest-microsite mock-up (test data, not linked anywhere).
        path: '/microsite-demo',
        element: (
          <Suspense fallback={<PageLoader />}>
            <MicrositeDemoPage />
          </Suspense>
        ),
      },
      {
        path: '/rsvp/:token',
        element: (
          <Suspense fallback={<PageLoader />}>
            <RsvpPage />
          </Suspense>
        ),
      },
      {
        path: '/p/:token',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PhotoUploadPage />
          </Suspense>
        ),
      },
      {
        // WhatsApp button short-link → real RSVP page
        path: '/r/:token',
        element: <TokenRedirect base="/rsvp" />,
      },
      {
        // WhatsApp media button short-link → real photo gallery page
        path: '/m/:token',
        element: <TokenRedirect base="/p" />,
      },
      {
        // Wallet ticket short-links (Apple / Google) from WhatsApp & email now
        // land on the universal QR ticket page — works in any in-app browser,
        // no "open in Safari" step needed to see the ticket.
        path: '/wa/:token',
        element: (
          <Suspense fallback={<PageLoader />}>
            <TicketPage />
          </Suspense>
        ),
      },
      {
        path: '/wg/:token',
        element: (
          <Suspense fallback={<PageLoader />}>
            <TicketPage />
          </Suspense>
        ),
      },
      {
        // Wallet-add redirects, linked from the ticket page's buttons. Apple
        // serves a .pkpass (opens Wallet in Safari; in-app browsers get an
        // "open in Safari" hint); Google forwards to its save URL.
        path: '/wallet/apple/:token',
        element: <WalletRedirect kind="apple" />,
      },
      {
        path: '/wallet/google/:token',
        element: <WalletRedirect kind="google" />,
      },
      {
        path: '/',
        element: (
          <Suspense fallback={<PageLoader />}>
            <LandingPage />
          </Suspense>
        ),
      },
      {
        path: '/impressum',
        element: (
          <Suspense fallback={<PageLoader />}>
            <LegalDocPage src="/legal/impressum.md" docName="Impressum" />
          </Suspense>
        ),
      },
      {
        path: '/hilfe',
        element: (
          <Suspense fallback={<PageLoader />}>
            <LegalDocPage src="/help/hilfe.md" docName="Hilfe" />
          </Suspense>
        ),
      },
      {
        path: '/datenschutz',
        element: (
          <Suspense fallback={<PageLoader />}>
            <LegalDocPage src="/legal/datenschutz.md" docName="Datenschutzerklärung" />
          </Suspense>
        ),
      },
      {
        path: '/nutzungsbedingungen',
        element: (
          <Suspense fallback={<PageLoader />}>
            <LegalDocPage src="/legal/nutzungsbedingungen.md" docName="Nutzungsbedingungen" />
          </Suspense>
        ),
      },
      {
        path: '/datenloeschung',
        element: (
          <Suspense fallback={<PageLoader />}>
            <LegalDocPage src="/legal/datenloeschung.md" docName="Datenlöschungs-Anleitung" />
          </Suspense>
        ),
      },
      {
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <InterfaceProvider>
                <AppLayout />
              </InterfaceProvider>
            </Suspense>
          </ProtectedRoute>
        ),
        children: [
          {
            path: '/dashboard',
            element: (
              <Suspense fallback={<PageLoader />}>
                <DashboardPage />
              </Suspense>
            ),
          },
          {
            path: 'events',
            element: (
              <Suspense fallback={<PageLoader />}>
                <EventsPage />
              </Suspense>
            ),
          },
          {
            path: 'events/new',
            element: (
              <ProtectedRoute requiredRoles={['ADMIN', 'ORGANIZER']}>
                <Suspense fallback={<PageLoader />}>
                  <NewEventPage />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: 'events/:eventId',
            element: (
              <Suspense fallback={<PageLoader />}>
                <EventDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'events/:eventId/settings',
            element: (
              <ProtectedRoute requiredRoles={['ADMIN', 'ORGANIZER']}>
                <Suspense fallback={<PageLoader />}>
                  <EventSettingsPage />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: 'guests',
            element: (
              <Suspense fallback={<PageLoader />}>
                <GuestsPage />
              </Suspense>
            ),
          },
          {
            path: 'guests/new',
            element: (
              <ProtectedRoute requiredRoles={['ADMIN', 'ORGANIZER', 'STAFF']}>
                <Suspense fallback={<PageLoader />}>
                  <NewGuestPage />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: 'guests/:guestId',
            element: (
              <Suspense fallback={<PageLoader />}>
                <GuestDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'users',
            element: <Navigate to="/settings" replace />,
          },
          {
            path: 'guests/import',
            element: (
              <ProtectedRoute requiredRoles={['ADMIN', 'ORGANIZER', 'STAFF']}>
                <Suspense fallback={<PageLoader />}>
                  <GuestImportPage />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: 'events/:eventId/invitations',
            element: (
              <Suspense fallback={<PageLoader />}>
                <EventInvitationsPage />
              </Suspense>
            ),
          },
          {
            path: 'events/:eventId/quick-edit',
            element: (
              <Suspense fallback={<PageLoader />}>
                <QuickEditPage />
              </Suspense>
            ),
          },
          {
            path: 'events/:eventId/messages',
            element: (
              <Suspense fallback={<PageLoader />}>
                <EventMessagesPage />
              </Suspense>
            ),
          },
          {
            path: 'events/:eventId/seating',
            element: (
              <Suspense fallback={<PageLoader />}>
                <EventSeatingPage />
              </Suspense>
            ),
          },
          {
            path: 'events/:eventId/accommodation',
            element: (
              <Suspense fallback={<PageLoader />}>
                <EventAccommodationPage />
              </Suspense>
            ),
          },
          {
            path: 'events/:eventId/checkin',
            element: (
              <ProtectedRoute requiredRoles={['ADMIN', 'ORGANIZER', 'STAFF']}>
                <Suspense fallback={<PageLoader />}>
                  <EventCheckInPage />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: 'events/:eventId/photos',
            element: (
              <ProtectedRoute requiredRoles={['ADMIN', 'ORGANIZER', 'STAFF']}>
                <Suspense fallback={<PageLoader />}>
                  <EventPhotosPage />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: 'templates',
            element: (
              <Suspense fallback={<PageLoader />}>
                <TemplatesPage />
              </Suspense>
            ),
          },
          {
            path: 'events/:eventId/templates',
            element: (
              <Suspense fallback={<PageLoader />}>
                <EventTemplatesPage />
              </Suspense>
            ),
          },
          {
            path: 'templates/:templateId',
            element: (
              <Suspense fallback={<PageLoader />}>
                <TemplateDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'inbox',
            element: (
              <Suspense fallback={<PageLoader />}>
                <InboxPage />
              </Suspense>
            ),
          },
          {
            path: 'settings',
            element: (
              <ProtectedRoute requiredRoles={['ADMIN']}>
                <Suspense fallback={<PageLoader />}>
                  <SettingsPage />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: '*',
        element: (
          <Suspense fallback={<PageLoader />}>
            <NotFoundPage />
          </Suspense>
        ),
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
